import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  Signal,
  signal,
  ViewChild,
  output,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';

import { WcgUploadFileRowComponent } from '../upload-file-row/upload-file-row.component';
import { MatButtonModule } from '@angular/material/button';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  defaultAllowedFileTypes,
  fileNameLengthValidator,
  fileSizeValidator,
  fileTypeValidator,
  forbiddenCharacters,
  forbiddenCharactersValidator,
} from '@shared/validators';

import {
  BannerType,
  WcgBannerComponent,
  WcgButtonComponent,
} from '@wcg/frontend-framework';
import { DocumentType, RequiredDocumentType } from '@documents/models';
import { WcgUploadFileComponent } from '@shared/components/upload-file/upload-file.component';
import { duplicateFileError, fileProcessingError } from '@shared/constants';
import { isDuplicateFileError } from '@admin/questionnaire/utils';
import { DocumentViewFacade } from '@submissions/v1/services';
import { findDocumentType } from '@data-access/submissions/utils/find-document-type.util';
import { validateFile } from '@shared/utils/upload-file-validator.util';

export interface UploadDocumentData {
  uploadtype: 'upload' | 'request' | 'reviewer_material';
  multiSelect?: boolean;
  requiredTypesForSubmission?: RequiredDocumentType[];
  requiredDocument?: RequiredDocumentType;
  submissionId: string;
  is_reviewer_document?: boolean;
  hasDuplicateFile?: boolean;
}

@Component({
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatExpansionModule,
    MatIconModule,
    ReactiveFormsModule,
    WcgButtonComponent,
    WcgUploadFileRowComponent,
    WcgUploadFileComponent,
    WcgBannerComponent,
  ],
  selector: 'wcg-upload-document',
  styleUrls: ['./upload-document.component.css'],
  templateUrl: './upload-document.component.html',
})
export class WcgUploadDocumentComponent implements AfterViewInit {
  private readonly _fb = inject(FormBuilder);
  private readonly _documentViewFacade = inject(DocumentViewFacade);
  private readonly dialogRef = inject(MatDialogRef<WcgUploadDocumentComponent>);
  public readonly data = inject<UploadDocumentData>(MAT_DIALOG_DATA);

  @ViewChild('titleRef')
  titleRef!: ElementRef<HTMLHeadingElement>;
  @ViewChild('uploadFileComponent')
  uploadFileComponent!: WcgUploadFileComponent;
  documentTypes: Signal<DocumentType[]> = computed(() =>
    this._documentViewFacade.$documentTypes(),
  );
  requiredTypesForSubmission: RequiredDocumentType[] = [];
  requiredDocument: RequiredDocumentType = {} as RequiredDocumentType;
  multiSelect = true;
  bannerClose = false;
  bannerType = BannerType;
  bannerText = signal<string>(duplicateFileError());
  submissionId = '';
  isReviewerMaterial = signal<boolean>(false);

  readonly handleApiValidation = output<File[]>();

  allowedFileTypes = defaultAllowedFileTypes
    .map((type: string) => `.${type}`)
    .join(', ');

  forbiddenCharacters = forbiddenCharacters.join(', ');

  uploadedFileForm = this._fb.group({
    files: this._fb.array([], [Validators.minLength(1)]),
  });

  get uploadedFiles(): FormGroup[] {
    return (this.uploadedFileForm.get('files') as FormArray)
      .controls as FormGroup[];
  }

  constructor() {
    // Get document types from the signal
    this.multiSelect = this.data.multiSelect ?? true;
    this.requiredTypesForSubmission =
      this.data.requiredTypesForSubmission || [];
    this.requiredDocument =
      this.data.requiredDocument || ({} as RequiredDocumentType);
    this.dialogRef.disableClose = true;
    this.submissionId = this.data.submissionId;
    this.isReviewerMaterial.set(this.data.is_reviewer_document || false);
    if (this.data.hasDuplicateFile) this.bannerClose = true;
  }

  get files(): FormArray<FormGroup> {
    return this.uploadedFileForm.get('files') as FormArray<FormGroup>;
  }

  get documentName(): string {
    return (
      this.documentTypes().find(
        (type) =>
          type.id === this.files.controls[0]?.get('documentType')?.value,
      )?.name || ''
    );
  }

  ngAfterViewInit() {
    if (this.titleRef) {
      this.titleRef.nativeElement.focus();
    }
  }

  onDeleteFile(index: number) {
    this.files.removeAt(index);
    this.uploadFileComponent.deleteFile();
    this.bannerClose = false;
  }

  handleCancel() {
    this.dialogRef.close();
  }

  handleConfirm() {
    if (this.uploadedFileForm.valid) {
      if (!this.submissionId) return;
      const files = this.uploadedFileForm.value.files as {
        file: File;
        documentType: string;
        documentName: string;
      }[];
      const documents = files.map((item) => {
        const documentType = findDocumentType(
          item.documentType,
          this.documentTypes(),
        );

        const requiredDocumentType = this.requiredTypesForSubmission.find(
          (docType) =>
            docType.document_type.id === documentType?.id &&
            item?.documentName === docType.document_name,
        );

        const documentName = item?.documentName ?? '';

        return {
          file: item.file,
          documentTypeId:
            documentType?.id || requiredDocumentType?.document_type.id || '',
          documentTypeName: documentType?.name || '',
          documentName,
          isAdditional: !requiredDocumentType,
          questionId: requiredDocumentType?.document_type.questionId,
          name: documentName,
          is_reviewer_document: this.isReviewerMaterial(),
        };
      });

      this.dialogRef.close();

      this._documentViewFacade
        .uploadDocuments(this.submissionId, documents)
        .subscribe({
          next: () => {},
          error: (err) => {
            if (isDuplicateFileError(err)) {
              this._documentViewFacade.openUploadDialog({
                data: {
                  documentTypes: this.documentTypes(),
                  hasDuplicateFile: true,
                  requiredTypesForSubmission: this.requiredTypesForSubmission,
                  submissionId: this.submissionId,
                },
                title: 'New Document',
              });
            }
          },
        });
    } else {
      this.uploadedFileForm.markAllAsTouched();
    }
  }

  get singleSelectSituation(): boolean {
    return this.multiSelect ? false : this.uploadedFiles.length > 1;
  }

  handleBannerClose() {
    this.bannerClose = false;
  }

  isDocumentTypeRequired(file: any): boolean {
    // Implement your logic to determine if this document type is required
    // For example, check if it's in a list of required types
    return (
      this.documentTypes()?.some(
        (type) =>
          type.id === file.documentTypeId || type.name === file.documentName,
      ) || false
    );
  }

  async fileUpload(files: File[]) {
    try {
      const validatedFiles = await this._duplicateFilesValidate(files);

      for (const { file, fileHash } of validatedFiles) {
        const fileFormGroup = this._fb.group({
          file: this._fb.control(file, [
            Validators.required,
            fileNameLengthValidator(256),
            fileSizeValidator(1),
            fileTypeValidator(),
            forbiddenCharactersValidator(),
          ]),
          documentType: this._fb.control(null, [Validators.required]),
          documentName: this._fb.control(''),
          questionId: this._fb.control(''),
          hash: this._fb.control(fileHash),
        });
        this.files.push(fileFormGroup);
      }
    } catch (error) {
      console.error('Error during file upload:', error);
      this.bannerText.set(fileProcessingError());
      this.bannerClose = true;
    }
  }

  private async _duplicateFilesValidate(
    files: File[],
  ): Promise<{ file: File; fileHash: string }[]> {
    const validatedFiles: { file: File; fileHash: string }[] = [];
    const existingFileNames = new Set(
      this.files.controls.map((control) => control.get('file')?.value.name),
    );
    const existingFileHashes = new Set(
      this.files.controls.map((control) => control.get('hash')?.value),
    );

    for (const file of files) {
      if (existingFileNames.has(file.name)) {
        this.bannerClose = true;
        continue;
      }

      if (validateFile(file) !== 'valid') {
        existingFileNames.add(file.name);
        validatedFiles.push({ file, fileHash: '' });
        continue;
      }

      const fileHash = await this._generateFileHash(file);
      if (existingFileHashes.has(fileHash)) {
        this.bannerClose = true;
        continue;
      }

      existingFileNames.add(file.name);
      existingFileHashes.add(fileHash);
      validatedFiles.push({ file, fileHash });
    }

    return validatedFiles;
  }

  private _generateFileHash(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);

      reader.onload = async () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray
          .map((byte) => byte.toString(16).padStart(2, '0'))
          .join('');
        resolve(hashHex);
      };

      reader.onerror = (error) => reject(error);
    });
  }
}
